/**
 * The cells as columns, on a tilted map (amendment 15).
 *
 * Every cell stands as a translucent column in its GDI colour, as tall as it is busy, and the
 * columns grow out of the ground while the map is being tilted: flat, this layer draws nothing
 * at all and the map is exactly what it always was. A swarm's members bounce and light up as
 * the pulse reaches them; the pulse itself, the ring at each foot and the arcs between the
 * tops, is `swarm-pulse.ts`, and both take their timing and heights from `cell-motion.ts`.
 *
 * A cell with a Wabenflagge (amendment 13) gets a tower on top: a mast, a red flag waving from
 * it and a gold knob, riding the column up and down. The flat map's 🚩 is a symbol, and MapLibre
 * cannot lift a symbol off the ground — so up here the flag is drawn, and down there the symbol
 * fades out as the columns rise (`DensityMap.svelte`).
 *
 * The flags never stop waving while they stand, which means the map draws continuously then —
 * so only then: tilted, with a flag among the cells, in the hexagon view, on a visible page and
 * with motion not reduced, and at 24 frames a second, which a cloth needs and a battery minds
 * less than 60. Flat, the loop does not run and the map is as still as it ever was.
 *
 * A WebGL layer for the same reason as the pulse: the height changes with the tilt and with the
 * pulse every frame, and a `fill-extrusion` would have MapLibre tile its source again for each
 * of those frames. In `3d` mode, so that it shares the depth buffer with the buildings and a
 * house in front hides the column behind it.
 */

import { boundaryOf, centreOf } from '$domain/h3.ts';
import { swarmsOf } from '$domain/swarm.ts';
import type { CustomLayerInterface, Map as MapLibreMap } from 'maplibre-gl';
import {
  columnMetres,
  FULL_STRENGTH,
  GLSL_MOTION,
  heartbeat,
  liftFor,
  MAX_HOP,
} from './cell-motion.ts';
import { gdiRgb } from './gdi-scale.ts';
import type { CellCollection } from './types.ts';
import {
  anchored,
  buildProgram,
  glsl,
  mercator,
  type Point,
  vertexArray,
} from './webgl.ts';

/**
 * Floats per column vertex: base (2), offset (2), up, normal (3), colour (3), height, hop,
 * strength and rim. `up` is 0 at the foot and 1 at the top; `rim` is 0 at the middle of the top
 * and 1 at its edge, which is what draws the bright line round it.
 */
const STRIDE = 15;
/** How opaque a column is: enough to read its colour, little enough to see the houses in it. */
const OPACITY = 0.62;
/** Where the light comes from, in the map's plane: the north-west, as on most maps. */
const LIGHT: Point = { x: -0.6, y: -0.8 };

/** The flag tower's parts. */
const TOWER = { mast: 0, cloth: 1, knob: 2 } as const;
/** Floats per tower vertex: base (2), part, u, v, column height, cell size, hop, strength. */
const TOWER_STRIDE = 9;
/** The mast's height and the flag's width and drop, in sizes of the cell. */
const MAST = 0.7;
const FLAG_WIDTH = 0.5;
const FLAG_DROP = 0.3;
/** Pieces the flag is cut into along its length, so that it can wave. */
const CLOTH_SEGMENTS = 8;
/** The mast's half-width and the knob's radius, in screen pixels: a hairline at any zoom. */
const MAST_PX = 1.3;
const KNOB_PX = 4;
/** MapLibre's world is 512 px wide at zoom 0. */
const WORLD_PX = 512;
/** At most one frame per this many milliseconds while a flag waves: 24 a second. */
const WAVE_FRAME_MS = 1000 / 24;

const VERTEX = `#version 300 es
precision highp float;
uniform mat4 u_matrix;
uniform float u_tau;
uniform float u_lift;
layout(location = 0) in vec2 a_base;
layout(location = 1) in vec2 a_offset;
layout(location = 2) in float a_up;
layout(location = 3) in vec3 a_normal;
layout(location = 4) in vec3 a_colour;
layout(location = 5) in float a_height;
layout(location = 6) in float a_hop;
layout(location = 7) in float a_strength;
layout(location = 8) in float a_rim;
out vec3 v_colour;
out float v_flash;
out float v_up;
out float v_rim;
out float v_top;
${GLSL_MOTION}
const vec2 LIGHT = vec2(${glsl(LIGHT.x)}, ${glsl(LIGHT.y)});
void main() {
  float amp = amplitude(a_hop, a_strength);
  float height = a_height * u_lift * (1.0 + BOUNCE * bounceOf(u_tau, a_hop) * amp);
  gl_Position = u_matrix * vec4(a_base + a_offset, a_up * height, 1.0);
  // The top in full colour; the walls darker the further they face away from the light.
  float shade = a_normal.z > 0.5 ? 1.0 : 0.6 + 0.4 * max(0.0, dot(a_normal.xy, LIGHT));
  v_colour = a_colour * shade;
  v_flash = flashOf(u_tau, a_hop) * amp;
  v_up = a_up;
  v_rim = a_rim;
  v_top = a_normal.z;
}`;

const FRAGMENT = `#version 300 es
precision highp float;
uniform float u_dark;
uniform float u_reveal;
in vec3 v_colour;
in float v_flash;
in float v_up;
in float v_rim;
in float v_top;
out vec4 fragColour;
void main() {
  vec3 colour = v_colour;
  // Walls fade towards the ground, which is what makes a column read as standing on it.
  if (v_top < 0.5) colour *= 0.65 + 0.35 * v_up;
  float rim = v_top > 0.5 ? smoothstep(0.82, 1.0, v_rim) : 0.0;
  vec3 light = mix(colour, vec3(1.0), 0.15 + 0.35 * u_dark);
  colour = mix(colour, light, clamp(v_flash + 0.55 * rim, 0.0, 1.0));
  float alpha = clamp(${glsl(OPACITY)} + 0.2 * v_flash, 0.0, 1.0) * u_reveal;
  fragColour = vec4(colour * alpha, alpha);
}`;

// The tower stands on the column's top and rises and bounces with it. The mast and the knob
// are a fixed number of pixels wide; the flag is sized with the cell, and always turned to
// face the viewer — a flag seen edge-on is a line — then waved: a wave running from the mast to
// the free end, its folds darker than its crests, driven by the clock in `u_time`.
const TOWER_VERTEX = `#version 300 es
precision highp float;
uniform mat4 u_matrix;
uniform float u_tau;
uniform float u_lift;
uniform float u_time;
uniform float u_px;
uniform vec2 u_right;
uniform float u_dark;
layout(location = 0) in vec2 a_base;
layout(location = 1) in float a_part;
layout(location = 2) in float a_u;
layout(location = 3) in float a_v;
layout(location = 4) in float a_height;
layout(location = 5) in float a_size;
layout(location = 6) in float a_hop;
layout(location = 7) in float a_strength;
out vec3 v_colour;
out float v_glow;
${GLSL_MOTION}
void main() {
  float amp = amplitude(a_hop, a_strength);
  float top = a_height * u_lift * (1.0 + BOUNCE * bounceOf(u_tau, a_hop) * amp);
  float mast = ${glsl(MAST)} * a_size * u_lift;
  vec2 forward = vec2(u_right.y, -u_right.x);
  int part = int(a_part + 0.5);
  vec3 position;
  if (part == ${TOWER.mast}) {
    position = vec3(a_base + u_right * (a_u * ${glsl(MAST_PX)} * u_px), top + a_v * mast);
    v_colour = mix(vec3(0.29, 0.31, 0.34), vec3(0.86, 0.86, 0.89), u_dark);
    v_glow = 0.0;
  } else if (part == ${TOWER.cloth}) {
    float width = ${glsl(FLAG_WIDTH)} * a_size * u_lift;
    float drop = ${glsl(FLAG_DROP)} * a_size * u_lift;
    float phase = a_u * 8.2 - u_time * 5.0;
    float wave = sin(phase) * a_u;
    position = vec3(
      a_base + u_right * (a_u * width) + forward * (wave * 0.18 * width),
      top + mast - a_v * drop + wave * 0.12 * drop
    );
    v_colour = vec3(1.0, 0.302, 0.31) * (0.78 + 0.22 * cos(phase));
    v_glow = flashOf(u_tau, a_hop) * amp;
  } else {
    float radius = ${glsl(KNOB_PX)} * u_px;
    position = vec3(a_base + u_right * (a_u * radius), top + mast + radius * (1.0 + a_v));
    v_colour = vec3(1.0, 0.82, 0.4);
    v_glow = flashOf(u_tau, a_hop) * amp;
  }
  gl_Position = u_matrix * vec4(position, 1.0);
}`;

const TOWER_FRAGMENT = `#version 300 es
precision highp float;
in vec3 v_colour;
in float v_glow;
out vec4 fragColour;
void main() {
  fragColour = vec4(mix(v_colour, vec3(1.0), clamp(0.6 * v_glow, 0.0, 1.0)), 1.0);
}`;

type Geometry = {
  columns: Float32Array;
  columnCount: number;
  towers: Float32Array;
  towerCount: number;
  anchor: Point;
};

const NOTHING: Geometry = {
  columns: new Float32Array(0),
  columnCount: 0,
  towers: new Float32Array(0),
  towerCount: 0,
  anchor: { x: 0, y: 0 },
};

/** A column for every cell and a tower for every flag, built once per change of the data. */
function geometryOf(collection: CellCollection | undefined): Geometry {
  const features = collection?.features ?? [];
  const first = features[0];
  if (first === undefined) return NOTHING;

  // Which cells bounce, how far out they are and how strong their swarm is.
  const swarmOf = new Map<string, { hop: number; strength: number }>();
  for (const swarm of swarmsOf(features.map(({ properties }) => properties))) {
    const strength = Math.min(1, swarm.total / FULL_STRENGTH);
    for (const member of swarm.members) {
      swarmOf.set(member.cell, { hop: Math.min(member.hop, MAX_HOP), strength });
    }
  }

  const anchor = mercator(centreOf(first.properties.cell));
  const columns: number[] = [];
  const towers: number[] = [];
  for (const { properties } of features) {
    const position = centreOf(properties.cell);
    const at = mercator(position);
    const centre = { x: at.x - anchor.x, y: at.y - anchor.y };
    const corners = boundaryOf(properties.cell)
      .slice(0, -1)
      .map(([lon, lat]) => {
        const corner = mercator({ lat, lon });
        return { x: corner.x - at.x, y: corner.y - at.y };
      });
    const colour = gdiRgb(properties.gdi);
    const height = columnMetres(properties.cell, properties.gdi) * at.metre;
    const { hop, strength } = swarmOf.get(properties.cell) ?? { hop: -1, strength: 0 };

    const vertex = (
      offset: Point,
      up: number,
      normal: [number, number, number],
      rim: number,
    ) => {
      columns.push(
        centre.x,
        centre.y,
        offset.x,
        offset.y,
        up,
        ...normal,
        ...colour,
        height,
        hop,
        strength,
        rim,
      );
    };

    corners.forEach((corner, index) => {
      const next = corners[(index + 1) % corners.length] ?? corner;
      // The top, a fan from the middle.
      vertex({ x: 0, y: 0 }, 1, [0, 0, 1], 0);
      vertex(corner, 1, [0, 0, 1], 1);
      vertex(next, 1, [0, 0, 1], 1);
      // The wall under this edge, facing out from the middle.
      const ex = next.x - corner.x;
      const ey = next.y - corner.y;
      const length = Math.hypot(ex, ey) || 1;
      let normal: [number, number, number] = [ey / length, -ex / length, 0];
      if (normal[0] * (corner.x + next.x) + normal[1] * (corner.y + next.y) < 0) {
        normal = [-normal[0], -normal[1], 0];
      }
      vertex(corner, 0, normal, 0);
      vertex(next, 0, normal, 0);
      vertex(corner, 1, normal, 0);
      vertex(next, 0, normal, 0);
      vertex(next, 1, normal, 0);
      vertex(corner, 1, normal, 0);
    });

    if (properties.flagHolder === undefined) continue;

    const corner = corners[0] ?? { x: 0, y: 0 };
    const size = Math.hypot(corner.x, corner.y);
    const part = (kind: number, u: number, v: number) => {
      towers.push(centre.x, centre.y, kind, u, v, height, size, hop, strength);
    };
    const quad = (kind: number, [u0, v0]: [number, number], [u1, v1]: [number, number]) => {
      part(kind, u0, v0);
      part(kind, u1, v0);
      part(kind, u0, v1);
      part(kind, u1, v0);
      part(kind, u1, v1);
      part(kind, u0, v1);
    };
    // The mast: across in pixels (u = −1…1), up from the roof to its full height (v = 0…1).
    quad(TOWER.mast, [-1, 0], [1, 1]);
    // The flag: out from the mast (u = 0…1), down from its top edge (v = 0…1).
    for (let piece = 0; piece < CLOTH_SEGMENTS; piece += 1) {
      quad(TOWER.cloth, [piece / CLOTH_SEGMENTS, 0], [(piece + 1) / CLOTH_SEGMENTS, 1]);
    }
    // The knob, a diamond on the tip.
    part(TOWER.knob, 0, 1);
    part(TOWER.knob, 1, 0);
    part(TOWER.knob, 0, -1);
    part(TOWER.knob, 0, 1);
    part(TOWER.knob, 0, -1);
    part(TOWER.knob, -1, 0);
  }

  return {
    columns: new Float32Array(columns),
    columnCount: columns.length / STRIDE,
    towers: new Float32Array(towers),
    towerCount: towers.length / TOWER_STRIDE,
    anchor,
  };
}

type Drawing = {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject;
  buffer: WebGLBuffer;
  uniform: (name: string) => WebGLUniformLocation | null;
};

function drawing(
  gl: WebGL2RenderingContext,
  vertex: string,
  fragment: string,
  layout: readonly (readonly [number, number, number])[],
  stride: number,
): Drawing {
  const program = buildProgram(gl, vertex, fragment);
  const buffer = gl.createBuffer();
  if (buffer === null) throw new Error('no buffer');
  const vao = vertexArray(gl, buffer, layout, stride);
  const uniforms = new Map<string, WebGLUniformLocation | null>();
  return {
    program,
    vao,
    buffer,
    uniform: (name) => {
      if (!uniforms.has(name)) uniforms.set(name, gl.getUniformLocation(program, name));
      return uniforms.get(name) ?? null;
    },
  };
}

export type CellColumns = {
  /** For `addLayer`: after the 3D buildings, so that each hides what is behind it. */
  layer: CustomLayerInterface;
  setCells(collection: CellCollection | undefined): void;
  /** Off while the map shows something other than the hexagons: no columns, no waving. */
  setEnabled(enabled: boolean): void;
  /**
   * How far the columns may show, from 0 to 1. Held at 0 while the big bang plays
   * (`cell-burst.ts`, amendment 17), whose own columns stand in for these and fade into them.
   */
  setReveal(share: number): void;
  destroy(): void;
};

export function createCellColumns({ id, dark }: { id: string; dark: boolean }): CellColumns {
  let map: MapLibreMap | undefined;
  let gpu: { columns: Drawing; towers: Drawing } | undefined;
  let broken = false;
  let geometry = NOTHING;
  let uploaded = false;
  let enabled = true;
  let reveal = 1;
  let frame: number | undefined;
  let lastFrame = 0;

  const motion =
    typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : undefined;

  /** Whether a flag stands and should be waving right now. */
  const waving = () =>
    enabled &&
    map !== undefined &&
    geometry.towerCount > 0 &&
    liftFor(map.getPitch()) > 0 &&
    motion?.matches !== true &&
    document.visibilityState === 'visible';

  function wave(now: number) {
    frame = undefined;
    if (!waving()) return;
    if (now - lastFrame >= WAVE_FRAME_MS) {
      lastFrame = now;
      map?.triggerRepaint();
    }
    frame = requestAnimationFrame(wave);
  }

  /**
   * Start waving if a flag should. Called wherever that can have become true: a frame drawn
   * after a tilt, new cells, the hexagon view switched on, the page shown again. Stopping needs
   * no caller — `wave` checks on every frame and simply does not ask for another.
   */
  function startWaving() {
    if (frame === undefined && waving()) frame = requestAnimationFrame(wave);
  }

  const onVisibility = () => startWaving();
  const onMotion = () => {
    startWaving();
    map?.triggerRepaint();
  };
  document.addEventListener('visibilitychange', onVisibility);
  motion?.addEventListener('change', onMotion);

  /** As in `swarm-pulse.ts`: made on the first frame, and a failure costs the columns only. */
  function prepare(gl: WebGL2RenderingContext) {
    if (gpu !== undefined || broken) return gpu;
    try {
      gpu = {
        columns: drawing(
          gl,
          VERTEX,
          FRAGMENT,
          [
            [0, 2, 0],
            [1, 2, 2],
            [2, 1, 4],
            [3, 3, 5],
            [4, 3, 8],
            [5, 1, 11],
            [6, 1, 12],
            [7, 1, 13],
            [8, 1, 14],
          ],
          STRIDE,
        ),
        towers: drawing(
          gl,
          TOWER_VERTEX,
          TOWER_FRAGMENT,
          [
            [0, 2, 0],
            [1, 1, 2],
            [2, 1, 3],
            [3, 1, 4],
            [4, 1, 5],
            [5, 1, 6],
            [6, 1, 7],
            [7, 1, 8],
          ],
          TOWER_STRIDE,
        ),
      };
      uploaded = false;
    } catch (error) {
      broken = true;
      console.error('cell columns disabled', error);
    }
    return gpu;
  }

  const layer: CustomLayerInterface = {
    id,
    type: 'custom',
    renderingMode: '3d',
    onAdd(added) {
      map = added;
    },
    onRemove(_removed, gl) {
      for (const part of gpu === undefined ? [] : [gpu.columns, gpu.towers]) {
        gl.deleteProgram(part.program);
        gl.deleteVertexArray(part.vao);
        gl.deleteBuffer(part.buffer);
      }
      gpu = undefined;
      map = undefined;
    },
    render(gl, options) {
      if (map === undefined || geometry.columnCount === 0 || reveal <= 0) return;
      // Flat, there is nothing to stand up: the map stays the 2D one, and costs nothing here.
      const lift = liftFor(map.getPitch());
      if (lift <= 0) return;
      const projection = map.getProjection()?.type;
      if (projection !== undefined && projection !== 'mercator') return;
      const ready = prepare(gl);
      if (ready === undefined) return;

      if (!uploaded) {
        gl.bindBuffer(gl.ARRAY_BUFFER, ready.columns.buffer);
        gl.bufferData(gl.ARRAY_BUFFER, geometry.columns, gl.STATIC_DRAW);
        gl.bindBuffer(gl.ARRAY_BUFFER, ready.towers.buffer);
        gl.bufferData(gl.ARRAY_BUFFER, geometry.towers, gl.STATIC_DRAW);
        uploaded = true;
      }
      const matrix = anchored(options.defaultProjectionData.mainMatrix, geometry.anchor);
      const still = motion?.matches === true;
      const tau = still ? -1e4 : heartbeat(performance.now());
      gl.enable(gl.DEPTH_TEST);
      gl.depthFunc(gl.LEQUAL);

      // The towers first, solid: the translucent columns drawn after them then show a flag
      // behind them through the glass, as they show the houses. Solid cannot fade, so after a
      // big bang the towers wait for the columns to be all there and then stand on them.
      if (geometry.towerCount > 0 && reveal >= 1) {
        const towers = ready.towers;
        const bearing = (map.getBearing() * Math.PI) / 180;
        gl.useProgram(towers.program);
        gl.bindVertexArray(towers.vao);
        gl.uniformMatrix4fv(towers.uniform('u_matrix'), false, matrix);
        gl.uniform1f(towers.uniform('u_tau'), tau);
        gl.uniform1f(towers.uniform('u_lift'), lift);
        // With motion reduced the flag hangs still in its folds; otherwise the clock drives it,
        // and `startWaving` keeps the frames coming.
        gl.uniform1f(towers.uniform('u_time'), still ? 0 : performance.now() / 1000);
        startWaving();
        gl.uniform1f(towers.uniform('u_px'), 1 / (WORLD_PX * 2 ** map.getZoom()));
        // The viewer's right on the ground: east at bearing 0, south at 90.
        gl.uniform2f(towers.uniform('u_right'), Math.cos(bearing), Math.sin(bearing));
        gl.uniform1f(towers.uniform('u_dark'), dark ? 1 : 0);
        gl.disable(gl.BLEND);
        gl.depthMask(true);
        gl.drawArrays(gl.TRIANGLES, 0, geometry.towerCount);
      }

      const columns = ready.columns;
      gl.useProgram(columns.program);
      gl.bindVertexArray(columns.vao);
      gl.uniformMatrix4fv(columns.uniform('u_matrix'), false, matrix);
      gl.uniform1f(columns.uniform('u_tau'), tau);
      gl.uniform1f(columns.uniform('u_lift'), lift);
      gl.uniform1f(columns.uniform('u_dark'), dark ? 1 : 0);
      gl.uniform1f(columns.uniform('u_reveal'), reveal);

      // First the depth of the nearest faces, then their colour where they are nearest: a
      // translucent column shows the street behind it, but not its own back walls.
      gl.disable(gl.BLEND);
      gl.colorMask(false, false, false, false);
      gl.depthMask(true);
      gl.drawArrays(gl.TRIANGLES, 0, geometry.columnCount);

      gl.colorMask(true, true, true, true);
      gl.depthMask(false);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.drawArrays(gl.TRIANGLES, 0, geometry.columnCount);
      gl.bindVertexArray(null);
    },
  };

  return {
    layer,
    setCells(collection) {
      geometry = geometryOf(collection);
      uploaded = false;
      map?.triggerRepaint();
      startWaving();
    },
    setEnabled(value) {
      enabled = value;
      startWaving();
    },
    setReveal(share) {
      reveal = share;
    },
    destroy() {
      if (frame !== undefined) cancelAnimationFrame(frame);
      frame = undefined;
      document.removeEventListener('visibilitychange', onVisibility);
      motion?.removeEventListener('change', onMotion);
      map = undefined;
    },
  };
}
